import { currencyFormatOptions } from '@ticketmaster/tm1pos-web-shared/constants';
import mainMessages from '@ticketmaster/tm1pos-web-shared/messages';
import Big from 'big.js';
import classNames from 'classnames';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, type InjectedIntl, injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { change, Field } from 'redux-form/immutable';
import messages from '../../../../messages';
import { CHECKOUT_FORM_TITLE } from '../../constants';
import { CurrencyInput } from './currency-input/currency-input';
import type { FieldNames } from '../../checkout-form';

export type CashFormProps = {
  cashToTender: {
    amount: number;
    currency: string;
  };
  fieldNames: FieldNames;
  intl: InjectedIntl;
  getFieldValue: (fieldName: string) => string | number;
};

const CashFormComponent = (props: CashFormProps) => {
  const { fieldNames, cashToTender, intl, getFieldValue } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    // Artificially set changeDue to 0 on render or rerender
    const changeDue = countChangeDueAmount(cashToTender?.amount, cashToTender?.amount);
    dispatch(change(CHECKOUT_FORM_TITLE, fieldNames.cashTendered, cashToTender?.amount));
    dispatch(change(CHECKOUT_FORM_TITLE, fieldNames.changeDue, changeDue));
  }, [cashToTender, dispatch, fieldNames.cashTendered, fieldNames.changeDue]);

  const onCashTenderedChange = (value: string) => {
    const changeDue = countChangeDueAmount(value, cashToTender?.amount);
    dispatch(change(CHECKOUT_FORM_TITLE, fieldNames.cashTendered, value));
    dispatch(change(CHECKOUT_FORM_TITLE, fieldNames.changeDue, changeDue));
  };

  const getCashTenderedValue = () => getFieldValue(fieldNames.cashTendered);

  const getChangeDueValue = () => getFieldValue(fieldNames.changeDue);

  const getCashFormLabels = () => {
    const cashTenderedLabel = intl.formatMessage(mainMessages.cashTendered);
    const changeDueLabel = intl.formatMessage(mainMessages.changeDue);

    return {
      cashTenderedLabel,
      changeDueLabel,
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const countChangeDueAmount = (cashTendered: string | number, cashToTender: number) =>
    Math.max(0, new Big(cashTendered).minus(cashToTender) as unknown as number);

  const validateCashTenderedField = useCallback(
    (cashTenderedValue) => {
      const isValid = parseFloat(cashTenderedValue) >= cashToTender?.amount;
      const message = <FormattedMessage {...messages.notEnoughCashError} />;
      return isValid ? false : message;
    },
    [cashToTender],
  );

  const hasChange = !!getChangeDueValue();
  const { cashTenderedLabel, changeDueLabel } = getCashFormLabels();
  const cashTenderedValue = getCashTenderedValue();

  return (
    <div className="sdr-checkout__cash">
      <Field
        name={fieldNames.cashTendered}
        validate={validateCashTenderedField}
        component={CurrencyInput}
        cashTenderedLabel={cashTenderedLabel}
        cashTenderedValue={cashTenderedValue}
        onValueChange={onCashTenderedChange}
        locale={intl.locale}
        currency={cashToTender?.currency}
      />
      <div className={classNames('sdr-checkout__change-due', { 'sdr-checkout__change-due--positive': hasChange })}>
        <label className="oc-label">{changeDueLabel}</label>
        <div className="oc-textfield">
          {/* @ts-expect-error - getChangeValueDue can be an empty string instead of a number */}
          {intl.formatNumber(getChangeDueValue(), {
            ...currencyFormatOptions,
            currency: cashToTender?.currency,
          })}
        </div>
      </div>
    </div>
  );
};

export const CashForm = injectIntl(CashFormComponent);
